/**
 * Spectrogram — the same decay curve as Waterfall, reshaped into a time x
 * frequency heatmap instead of a cascade of polylines. Paint layer only —
 * see `../waterfall.ts` for computeSpectrogramGeometry (the grid reshape
 * math is shared with the waterfall geometry file since both read the same
 * RT60DecayCurve).
 *
 * Mirrors web's `web/src/components/room/charts/waterfallDraw.ts`
 * `drawSpectrogram`, ported to mobile's light ink convention.
 */

import { canvasTypeFont } from '@roomtreatment/design';
import { setupCanvas, drawEmptyMessage, drawCells, INK, withAlpha, intensityAlpha, type DrawCell } from './_shared';
import { computeSpectrogramGeometry } from '../waterfall';
import { formatHz } from '../ticks';
import type { ChartPad } from '../layout';
import type { RT60DecayCurve } from '../registry';

export const SPECTROGRAM_PAD: ChartPad = { t: 10, r: 14, b: 22, l: 44 };

export interface DrawSpectrogramOpts {
  decay: RT60DecayCurve;
  width: number;
  height: number;
  maxTimeS?: number;
  timeSteps?: number;
}

export function drawSpectrogramCanvas(canvas: HTMLCanvasElement, opts: DrawSpectrogramOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeSpectrogramGeometry({
    decay: opts.decay,
    width: opts.width,
    height: opts.height,
    pad: SPECTROGRAM_PAD,
    maxTimeS: opts.maxTimeS,
    timeSteps: opts.timeSteps,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No decay curve yet — ≥200 Hz only');
    return;
  }

  const { frame } = geo;
  // 0 dB (loudest, t=0) = full ink; -60 dB (decayed away) = paper — same
  // fixed -60..0 dB display range computeSpectrogramGeometry clamps to.
  const cells: DrawCell[] = geo.cells.map((cell) => ({
    x0: cell.x0,
    y0: cell.y0,
    x1: cell.x1,
    y1: cell.y1,
    color: withAlpha(INK.primary, intensityAlpha(cell.db, 60)),
  }));
  drawCells(ctx, frame, cells);

  ctx.font = canvasTypeFont('tick');
  ctx.fillStyle = INK.label;
  ctx.textAlign = 'right';
  for (const bl of geo.bandLabels) {
    ctx.fillText(formatHz(bl.hz), frame.plotX - 4, frame.plotY + bl.y + 3);
  }
  ctx.textAlign = 'center';
  for (const tl of geo.timeLabels) {
    ctx.fillText(`${Math.round(tl.timeS * 1000)}`, frame.plotX + tl.x, frame.plotY + frame.plotH + 12);
  }
}
